import { supabase } from './supabase';

/**
 * Thin wrapper around fetch() that attaches the current Supabase
 * access token as a Bearer header. Pages Functions inspect it in
 * `_middleware.ts` and 401 before any handler runs if the user is
 * unauthenticated or not an admin.
 *
 * Browsers don't share localStorage with Cloudflare Pages, so we
 * can't rely on cookies — the access token has to ride the request.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers(init.headers ?? {});
  // Only default Content-Type when the caller hasn't set one AND the
  // body isn't FormData. FormData posts MUST let the browser set the
  // multipart boundary automatically — overriding here would land a
  // body the server can't parse (Pages Function 400s on
  // request.formData()).
  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  return fetch(path, { ...init, headers });
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text || path}`);
  }
  return res.json() as Promise<T>;
}
